import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Eye, EyeOff } from 'lucide-react';
import { authService } from '../services/authService';
import { useForm } from '../hooks/useForm';

export default function SignIn() {
  const navigate = useNavigate();
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showForgotModal, setShowForgotModal] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotMessage, setForgotMessage] = useState('');

  const form = useForm(
    { email: '', password: '' },
    async (values) => {
      setLoading(true);
      try {
        await authService.signin(values.email, values.password);
        navigate('/dashboard');
      } catch (err) {
        setError(err.message || 'Failed to sign in');
      } finally {
        setLoading(false);
      }
    }
  );

  const handleForgotPassword = async () => {
    if (!forgotEmail.trim()) {
      setForgotMessage('Please enter your email address');
      return;
    }

    setForgotLoading(true);
    setForgotMessage('');

    try {
      await authService.sendPasswordReset(forgotEmail);
      setForgotMessage('✅ Password reset link sent to your email. Check your inbox!');
      setTimeout(() => {
        setShowForgotModal(false);
        setForgotEmail('');
        setForgotMessage('');
      }, 3000);
    } catch (err) {
      setForgotMessage('❌ Error: ' + (err.message || 'Failed to send reset email'));
    } finally {
      setForgotLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-black text-white flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center justify-center gap-3 mb-8 hover:opacity-80 transition">
          <img src="/images/fhni%20logo.png" alt="Fire Hive Network" className="h-10 w-10 object-contain" />
          <h1 className="text-3xl font-black">FIRE HIVE</h1>
        </Link>

        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-gray-400 hover:text-white transition mb-6"
        >
          <ArrowLeft size={18} />
          Back
        </button>

        <div className="bg-zinc-900 p-8 rounded-lg border border-zinc-800">
          <h2 className="text-2xl font-bold mb-6">Welcome Back</h2>

          {error && (
            <div className="bg-red-500/20 border border-red-500/50 text-red-300 p-4 rounded-lg mb-6">
              {error}
            </div>
          )}

          <form onSubmit={form.handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">Email</label>
              <input
                type="email"
                name="email"
                value={form.values.email}
                onChange={form.handleChange}
                className="w-full px-4 py-2 bg-zinc-800 border border-zinc-700 rounded-lg focus:border-orange-500 outline-none transition"
                placeholder="your@email.com"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Password</label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  value={form.values.password}
                  onChange={form.handleChange}
                  className="w-full px-4 py-2 bg-zinc-800 border border-zinc-700 rounded-lg focus:border-orange-500 outline-none transition"
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-200 transition"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              <button
                type="button"
                onClick={() => setShowForgotModal(true)}
                className="text-sm text-orange-500 hover:text-orange-400 mt-2 transition"
              >
                Forgot Password?
              </button>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-orange-600 hover:bg-orange-700 disabled:opacity-50 text-white font-bold py-3 rounded-lg transition uppercase"
            >
              {loading ? 'Signing In...' : 'Sign In'}
            </button>
          </form>

          <p className="text-center text-gray-400 mt-6">
            Don't have an account?{' '}
            <Link to="/signup" className="text-orange-500 hover:text-orange-400 font-semibold">
              Sign Up
            </Link>
          </p>
        </div>

        {/* Forgot Password Modal */}
        {showForgotModal && (
          <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
            <div className="bg-zinc-900 p-8 rounded-lg border border-zinc-800 max-w-sm w-full mx-4">
              <h3 className="text-2xl font-bold mb-4">Reset Password</h3>
              
              <p className="text-gray-400 mb-4">
                Enter your email address and we'll send you a link to reset your password.
              </p>

              {forgotMessage && (
                <div className={`p-4 rounded-lg mb-4 ${
                  forgotMessage.includes('✅') 
                    ? 'bg-green-500/20 border border-green-500/50 text-green-300' 
                    : 'bg-red-500/20 border border-red-500/50 text-red-300'
                }`}>
                  {forgotMessage}
                </div>
              )}

              <input
                type="email"
                value={forgotEmail}
                onChange={(e) => setForgotEmail(e.target.value)}
                placeholder="your@email.com"
                className="w-full px-4 py-2 bg-zinc-800 border border-zinc-700 rounded-lg focus:border-orange-500 outline-none transition mb-4"
              />

              <div className="flex gap-3">
                <button
                  onClick={handleForgotPassword}
                  disabled={forgotLoading}
                  className="flex-1 bg-orange-600 hover:bg-orange-700 disabled:opacity-50 text-white font-bold py-2 rounded-lg transition"
                >
                  {forgotLoading ? 'Sending...' : 'Send Reset Link'}
                </button>
                <button
                  onClick={() => {
                    setShowForgotModal(false);
                    setForgotEmail('');
                    setForgotMessage('');
                  }}
                  className="flex-1 bg-zinc-800 hover:bg-zinc-700 text-white font-bold py-2 rounded-lg transition"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
